/**
 * Feedback Splash Data — Particle definitions and display config builder.
 *
 * Extracted from FeedbackSplash.tsx (667→~490 lines) during CL-1 s64.
 * Pure data + pure function — no React dependency.
 */

import type { DjayEmotion, DjayTier, DjaySkin } from '../../../mascot'

// ── Particle Types ───────────────────────────────────────────

export interface ConfettiParticle {
  id: number
  color: string
  size: string
  startX: number
  startY: number
  endX: number
  endY: number
  rotation: number
  delay: number
  duration: number
}

export interface WarmParticle {
  id: number
  color: string
  size: string
  startX: number
  startY: number
  endY: number
  delay: number
  duration: number
}

// ── Particle Generators ──────────────────────────────────────

const CONFETTI_COLORS = [
  'bg-amber-400',
  'bg-orange-300',
  'bg-yellow-400',
  'bg-rose-300',
  'bg-indigo-300',
  'bg-emerald-300',
]

const EXTRA_CONFETTI_COLORS = [
  'bg-purple-400',
  'bg-fuchsia-300',
  'bg-pink-400',
  'bg-amber-300',
  'bg-yellow-300',
  'bg-indigo-400',
]

const WARM_COLORS = ['bg-orange-200', 'bg-amber-200', 'bg-yellow-200']

function generateConfetti(
  count: number,
  colors: string[],
  idOffset = 0,
): ConfettiParticle[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i + idOffset,
    color: colors[i % colors.length],
    size: Math.random() > 0.5 ? 'w-2.5 h-2.5' : 'w-2 h-2',
    startX: 50 + (Math.random() - 0.5) * (idOffset ? 40 : 30),
    startY: idOffset ? 40 : 35,
    endX: Math.random() * 100,
    endY: 90 + Math.random() * (idOffset ? 20 : 15),
    rotation: Math.random() * (idOffset ? 720 : 540) - (idOffset ? 360 : 270),
    delay: Math.random() * (idOffset ? 0.4 : 0.25),
    duration: (idOffset ? 1.5 : 1.2) + Math.random() * (idOffset ? 0.5 : 0.4),
  }))
}

export const CONFETTI_PARTICLES: ConfettiParticle[] = generateConfetti(
  12,
  CONFETTI_COLORS,
)
export const EXTRA_CONFETTI: ConfettiParticle[] = generateConfetti(
  12,
  EXTRA_CONFETTI_COLORS,
  100,
)

export const WARM_PARTICLES: WarmParticle[] = Array.from(
  { length: 6 },
  (_, i) => ({
    id: i,
    color: WARM_COLORS[i % 3],
    size: 'w-2 h-2',
    startX: 15 + Math.random() * 70,
    startY: 100 + Math.random() * 15,
    endY: -15,
    delay: i * 0.4,
    duration: 5 + Math.random() * 2,
  }),
)

// ── Display Config ───────────────────────────────────────────

export type SplashPriority =
  | 'levelUp'
  | 'tierEvolution'
  | 'skinUnlock'
  | 'dailyGoal'
  | 'streak'
  | 'correct'
  | 'incorrect'

export interface SplashDisplayConfig {
  mainMessage: string
  subMessage: string
  emotion: DjayEmotion
  duration: number
  gradient: string
  showExtraConfetti: boolean
}

interface SplashInput {
  isCorrect: boolean
  streak: number
  levelUp?: { level: number; xp: number; title: string } | null
  tierEvolution?: { from: DjayTier; to: DjayTier } | null
  skinUnlock?: { skin: DjaySkin; description: string } | null
  dailyGoalReached?: boolean
  streakMilestone?: { count: number; message: string } | null
  splashMsg: { main: string; sub: string }
}

const WARM_GRADIENT = '#FEF3C7'
const EVOLUTION_GRADIENT = '#F3E8FF'
const INCORRECT_GRADIENT = '#FFF7ED'

export function resolveSplashPriority(input: SplashInput): SplashPriority {
  const {
    isCorrect,
    levelUp,
    tierEvolution,
    skinUnlock,
    dailyGoalReached,
    streakMilestone,
  } = input
  if (levelUp && isCorrect) return 'levelUp'
  if (tierEvolution && isCorrect) return 'tierEvolution'
  if (skinUnlock && isCorrect) return 'skinUnlock'
  if (dailyGoalReached && isCorrect) return 'dailyGoal'
  if (streakMilestone && isCorrect) return 'streak'
  return isCorrect ? 'correct' : 'incorrect'
}

export function buildSplashDisplayConfig(
  input: SplashInput,
): SplashDisplayConfig {
  const priority = resolveSplashPriority(input)
  const { levelUp, skinUnlock, streakMilestone, splashMsg, streak } = input

  switch (priority) {
    case 'levelUp':
      return {
        mainMessage: `Level Up! Level ${levelUp?.level}! 🎉`,
        subMessage: levelUp?.title ?? '',
        emotion: 'proud',
        duration: 3000,
        gradient: WARM_GRADIENT,
        showExtraConfetti: true,
      }
    case 'tierEvolution':
      return {
        mainMessage: 'DJAY EVOLUEERT! ✨',
        subMessage: `Level ${levelUp?.level || ''} — ${levelUp?.title || ''} 🌟`,
        emotion: 'proud',
        duration: 3500,
        gradient: EVOLUTION_GRADIENT,
        showExtraConfetti: true,
      }
    case 'skinUnlock':
      return {
        mainMessage: 'NIEUW ONTGRENDELD! 🎨',
        subMessage: skinUnlock?.description ?? '',
        emotion: 'celebrating',
        duration: 3000,
        gradient: WARM_GRADIENT,
        showExtraConfetti: true,
      }
    case 'dailyGoal':
      return {
        mainMessage: 'Dagelijks doel gehaald! 🎯',
        subMessage: '+50 XP bonus!',
        emotion: 'celebrating',
        duration: 2500,
        gradient: WARM_GRADIENT,
        showExtraConfetti: false,
      }
    case 'streak':
      return {
        mainMessage: streakMilestone?.message ?? '',
        subMessage: splashMsg.main,
        emotion: 'excited',
        duration: 2500,
        gradient: WARM_GRADIENT,
        showExtraConfetti: false,
      }
    case 'correct':
      return {
        mainMessage: splashMsg.main,
        subMessage: splashMsg.sub,
        emotion: streak >= 5 ? 'excited' : 'celebrating',
        duration: 2000,
        gradient: WARM_GRADIENT,
        showExtraConfetti: false,
      }
    case 'incorrect':
    default:
      return {
        mainMessage: splashMsg.main,
        subMessage: splashMsg.sub,
        emotion: 'encouraging',
        duration: 2500,
        gradient: INCORRECT_GRADIENT,
        showExtraConfetti: false,
      }
  }
}
